<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">
                        Add New Product Variant
                      </h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Create a new product variant with complete details and
                        pricing information
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Product Variants
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Product Variant Details Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-package-variant</v-icon
                    >
                    Product Variant Details
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="selectedProductName"
                        label="Product Name"
                        placeholder="Product name will be displayed here"
                        outlined
                        :error-messages="errors.product_id"
                        class="professional-field"
                        hide-details="auto"
                        readonly
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-package</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.name"
                        label="Product Variant Name"
                        placeholder="Enter product variant name"
                        outlined
                        :rules="[
                          (v) => !!v || 'Product variant name is required',
                        ]"
                        :error-messages="errors.name"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-package-variant</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <!-- <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.category_id"
                        :items="categories"
                        item-text="description"
                        item-value="id"
                        label="Category"
                        placeholder="Select category"
                        outlined
                        :error-messages="errors.category_id"
                        class="professional-field"
                        hide-details="auto"
                        clearable
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-tag</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col> -->

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.sku_id"
                        label="SKU ID"
                        placeholder="Enter unique SKU (optional)"
                        outlined
                        :error-messages="errors.sku_id"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-barcode</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Product Variant Units & Packing Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-package-variant</v-icon
                    >
                    Units & Packaging
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.unit_id"
                        :items="units"
                        item-text="name"
                        item-value="id"
                        label="Unit Type"
                        placeholder="Select unit (e.g., Tablet, ml, gm)"
                        outlined
                        :error-messages="errors.unit_id"
                        class="professional-field"
                        hide-details="auto"
                        clearable
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-scale</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.packing_type_id"
                        :items="packingTypes"
                        item-text="name"
                        item-value="id"
                        label="Packing Type"
                        placeholder="Select packing (e.g., Box, Bottle)"
                        outlined
                        :error-messages="errors.packing_type_id"
                        class="professional-field"
                        hide-details="auto"
                        clearable
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-package</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.potency"
                        label="Potency (Optional)"
                        placeholder="e.g., 30C, 200C, 1M"
                        outlined
                        :error-messages="errors.potency"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-flask</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.stock"
                        label="Stock Quantity (Optional)"
                        placeholder="Enter available stock"
                        outlined
                        type="number"
                        step="1"
                        min="0"
                        :rules="[
                          (v) =>
                            !v ||
                            parseInt(v) >= 0 ||
                            'Stock must be greater than or equal to 0',
                        ]"
                        :error-messages="errors.stock"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey"
                            >mdi-package-variant-closed</v-icon
                          >
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.tagline"
                        label="Product Variant Tagline (Optional)"
                        placeholder="Short catchy marketing tagline"
                        outlined
                        :error-messages="errors.tagline"
                        class="professional-field"
                        hide-details="auto"
                        maxlength="255"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-text-short</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Pricing Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-currency-inr</v-icon
                    >
                    Pricing & Tax Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.commission"
                        label="Commission (%)"
                        placeholder="Enter commission percentage (e.g., 10.00)"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        :error-messages="errors.commission"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-percent</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                </div>

                <!-- Physical Dimensions Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-cube-outline</v-icon
                    >
                    Physical Dimensions
                  </h3>

                  <v-row>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="form.length"
                        label="Length (cm)"
                        placeholder="Enter length"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :error-messages="errors.length"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-ruler</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="form.width"
                        label="Width (cm)"
                        placeholder="Enter width"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :error-messages="errors.width"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-ruler</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="form.height"
                        label="Height (cm)"
                        placeholder="Enter height"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :error-messages="errors.height"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-ruler</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="form.weight"
                        label="Weight (grams)"
                        placeholder="Weight including packing"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :error-messages="errors.weight"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-weight-gram</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Product Variant Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-information-outline</v-icon
                    >
                    Detailed Product Variant Information
                  </h3>

                  <v-row>
                    <v-col cols="12">
                      <div class="editor-field-container">
                        <label class="editor-label">
                          <v-icon color="grey" class="mr-2"
                            >mdi-note-text</v-icon
                          >
                          Additional Notes
                        </label>
                        <vue-editor
                          v-model="form.note"
                          :editor-toolbar="customToolbar"
                          placeholder="Any additional product variant notes..."
                          class="professional-editor"
                          :class="{ 'error--text': errors.note }"
                        />
                        <div v-if="errors.note" class="v-messages error--text">
                          <div class="v-messages__wrapper">
                            <div class="v-messages__message">
                              {{ errors.note[0] }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <!-- Product Variant Status Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-cog</v-icon>
                    Product Variant Status
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-card outlined class="status-card">
                        <v-card-text class="pa-2">
                          <div class="d-flex align-center">
                            <v-icon color="grey" class="mr-3"
                              >mdi-package-check</v-icon
                            >
                            <div class="flex-grow-1">
                              <div class="font-weight-medium mb-1">
                                Product Variant Status
                              </div>
                              <div class="text-caption grey--text">
                                Set the availability status for this product
                                variant
                              </div>
                            </div>
                            <v-switch
                              v-model="form.is_active"
                              color="success"
                              inset
                              hide-details
                              class="ml-3"
                            ></v-switch>
                          </div>
                          <div class="mt-2">
                            <v-chip
                              small
                              :color="form.is_active ? 'success' : 'warning'"
                              :text-color="form.is_active ? 'white' : 'black'"
                            >
                              {{ form.is_active ? "Active" : "Inactive" }}
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      small
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      :text-color="hasValidationErrors() ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{
                          hasValidationErrors()
                            ? "mdi-alert-circle"
                            : "mdi-check-circle"
                        }}
                      </v-icon>
                      {{
                        hasValidationErrors()
                          ? "Please fix validation errors"
                          : "Form validation passed"
                      }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      All required fields must be completed
                    </span>
                  </div>
                </v-col>

                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.back()"
                      :disabled="isSaving"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="saveProductVariant"
                      :loading="isSaving"
                      :disabled="isSaving || !valid"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Save Product Variant
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" max-width="900" persistent>
        <v-card>
          <v-card-title class="success-header py-3 px-4">
            <v-icon color="success" class="mr-3">mdi-check-circle</v-icon>
            <span class="text-h6">Product Variant Created Successfully!</span>
          </v-card-title>

          <v-card-text class="pa-4">
            <!-- Success Message -->
            <v-row class="mb-4">
              <v-col cols="12" class="text-center">
                <h3 class="text-success mb-2">
                  {{
                    createdProductVariant
                      ? createdProductVariant.name
                      : "Your product variant"
                  }}
                  has been created!
                </h3>
                <p class="text-body-1 grey--text text--darken-1">
                  Your product variant is now ready. You can upload images to
                  enhance the product variant listing.
                </p>
              </v-col>
            </v-row>

            <!-- Image Upload Section -->
            <v-row v-if="createdProductVariant">
              <v-col cols="12">
                <v-divider class="mb-4"></v-divider>
                <h4 class="mb-3 primary--text d-flex align-center">
                  <v-icon color="primary" class="mr-2"
                    >mdi-image-multiple</v-icon
                  >
                  Upload Product Variant Images
                  <v-chip x-small color="info" outlined class="ml-2"
                    >Optional</v-chip
                  >
                </h4>

                <!-- Product Variant Image Upload Component -->
                <ProductVariantImageUpload
                  :product-variant-id="createdProductVariant.id"
                  :readonly="false"
                  @images-uploaded="handleImagesUploaded"
                  @image-deleted="handleImageDeleted"
                  @images-loaded="handleImagesLoaded"
                />
              </v-col>
            </v-row>
          </v-card-text>

          <!-- Action Buttons -->
          <v-divider></v-divider>
          <v-card-actions class="pa-4">
            <div class="d-flex flex-wrap align-center justify-center w-100">
              <v-btn color="primary" class="ma-1" @click="editProductVariant">
                <v-icon left>mdi-pencil</v-icon>
                Edit Product Variant Details
              </v-btn>

              <v-btn
                color="info"
                outlined
                class="ma-1"
                @click="goToProductVariantsList"
              >
                <v-icon left>mdi-format-list-bulleted</v-icon>
                View All Product Variants
              </v-btn>

              <v-btn
                color="success"
                outlined
                class="ma-1"
                @click="createAnother"
              >
                <v-icon left>mdi-plus</v-icon>
                Create Another Product Variant
              </v-btn>
            </div>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import { VueEditor } from "vue2-editor";
import ProductVariantImageUpload from "@/components/ProductVariantImageUpload.vue";

export default {
  components: {
    VueEditor,
    ProductVariantImageUpload,
  },
  name: "ProductVariantCreatePage",
  data() {
    return {
      valid: false,
      form: {
        name: "",
        brand_name: "",
        sku_id: "",
        description: "",
        price: "",
        stock: 0,
        category_id: null,
        tag_ids: [],
        is_active: true,
        unit_id: null,
        packing_type_id: null,
        potency: "",
        commission: "",
        length: "",
        width: "",
        height: "",
        weight: "",
        ingredients: "",
        benefits: "",
        about: "",
        dosage: "",
        precautions: "",
        tagline: "",
        note: "",
      },
      isSaving: false,
      showSuccessDialog: false,
      createdProductVariant: null,
      productItems: [],
      categories: [],
      tags: [],
      units: [],
      packingTypes: [],
      productVariantImages: [],
      errors: {},
      customToolbar: [
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ["clean"],
        ["link"],
      ],
    };
  },

  computed: {
    selectedProductName() {
      console.log("Computing selectedProductName...");
      console.log("form.product_id:", this.form.product_id);
      console.log("productItems:", this.productItems);
      console.log("productItems.length:", this.productItems.length);
      
      if (this.form.product_id && this.productItems.length) {
        const productId = Number(this.form.product_id);
        console.log("Looking for productId:", productId);
        
        const selectedProduct = this.productItems.find(
          (product) => Number(product.id) === productId
        );
        console.log("Found selectedProduct:", selectedProduct);
        return selectedProduct ? selectedProduct.name : "";
      }
      return "";
    },
  },

  mounted() {
    this.getMasters();
    this.getTags();
  },

  methods: {
    hasValidationErrors() {
      return Object.keys(this.errors).length > 0 || !this.valid;
    },

    async getMasters() {
      try {
        const response = await axios.get("masters?context=product_variants");
        const masters = response.data;

        // Products
        if (masters.products) {
          this.productItems = masters.products;
          console.log("Loaded productItems:", this.productItems);

          const decryptedId = this.decryptIt(this.$route.params.productId);
          console.log("Route params (decrypted):", decryptedId);
          console.log("Setting form.product_id to:", Number(decryptedId));
          this.form.product_id = Number(decryptedId);
        }

        // categories
        if (masters.categories) {
          this.categories = masters.categories;
        }

        // Units
        if (masters.units) {
          this.units = masters.units.map((unit) => ({
            id: unit.id,
            name: unit.description,
            value: unit.id,
            text: unit.description,
          }));
        }

        // Packing Types
        if (masters.packing_types) {
          this.packingTypes = masters.packing_types.map((packingType) => ({
            id: packingType.id,
            name: packingType.description,
            value: packingType.id,
            text: packingType.description,
          }));
        }
      } catch (error) {
        console.log("Error loading masters data:", error);
      }
    },

    async saveProductVariant() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.isSaving = true;
      this.errors = {};

      try {
        // Prepare the data
        const productVariantData = {
          name: this.form.name,
          product_id: this.form.product_id || null,
          brand_name: this.form.brand_name || null,
          sku_id: this.form.sku_id || null,
          stock: parseFloat(this.form.stock) || 0,
          is_active: this.form.is_active,
          unit_id: this.form.unit_id,
          packing_type_id: this.form.packing_type_id,
          potency: this.form.potency || null,
          commission: this.form.commission ? parseFloat(this.form.commission) : null,
          length: this.form.length ? parseFloat(this.form.length) : null,
          width: this.form.width ? parseFloat(this.form.width) : null,
          height: this.form.height ? parseFloat(this.form.height) : null,
          weight: this.form.weight ? parseFloat(this.form.weight) : null,
          tagline: this.form.tagline,
          note: this.form.note,
        };

        const response = await axios.post(
          "/product_variants",
          productVariantData
        );

        if (response.data.success) {
          this.createdProductVariant = response.data.data;
          this.showSuccessDialog = true;
          // Don't reset form yet - let user decide what to do next
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while creating the product variant.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response) {
          if (error.response.status === 422) {
            // Validation errors
            this.errors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            // Authentication error
            this.$store.dispatch("errors/setError", {
              message: "Authentication required. Please login again.",
            });
            this.$router.push("/login");
          } else {
            // Other errors
            this.$store.dispatch("errors/setError", {
              message:
                error.response.data.message ||
                "An error occurred while creating the product variant.",
            });
          }
        } else {
          // Network or other errors
          this.$store.dispatch("errors/setError", {
            message:
              "Network error. Please check your connection and try again.",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    resetForm() {
      this.form = {
        name: "",
        brand_name: "",
        sku_id: "",
        description: "",
        price: "",
        stock: 0,
        category_id: null,
        tag_ids: [],
        is_active: true,
        unit_id: null,
        packing_type_id: null,
        potency: "",
        commission: "",
        length: "",
        width: "",
        height: "",
        weight: "",
        ingredients: "",
        benefits: "",
        about: "",
        dosage: "",
        precautions: "",
        tagline: "",
        note: "",
      };
      this.errors = {};
      this.$refs.form.resetValidation();
    },

    async getCategories() {
      try {
        const response = await axios.get("/product_variants/categories");
        if (response.data.success) {
          this.categories = response.data.data;
        }
      } catch (error) {
        console.log("Error loading categories:", error);
      }
    },

    async getTags() {
      try {
        const response = await axios.get("/tags/dropdown");
        if (response.data.success) {
          this.tags = response.data.data;
        }
      } catch (error) {
        console.log("Error loading tags:", error);
      }
    },

    // Success dialog actions
    editProductVariant() {
      if (this.createdProductVariant && this.createdProductVariant.id) {
        this.$router.push(
          `/products/${this.$route.params.productId}/product-variants/${this.createdProductVariant.id}?edit=true`
        );
      }
    },

    goToProductVariantsList() {
      this.showSuccessDialog = false;
      this.resetForm();
      this.$router.push(
        `/products/${this.$route.params.productId}/product-variants`
      );
    },

    createAnother() {
      this.showSuccessDialog = false;
      this.resetForm();
      this.createdProductVariant = null;
    },

    // Product Variant Image Event Handlers
    handleImagesUploaded(uploadedImages) {
      console.log("Images uploaded:", uploadedImages);
      this.$store.dispatch("errors/setError", {
        message: `${uploadedImages.length} image(s) uploaded successfully`,
        type: "success",
      });
    },

    handleImageDeleted(deletedImage) {
      console.log("Image deleted:", deletedImage);
      // Success message is already handled by the component
    },

    handleImagesLoaded(loadedImages) {
      console.log("Images loaded:", loadedImages);
      // Store images in component state if needed for other operations
      this.productVariantImages = loadedImages;
    },
  },

  watch: {
    // Clear errors when user starts typing
    "form.name"() {
      if (this.errors.name) {
        this.$delete(this.errors, "name");
      }
    },
    "form.brand_name"() {
      if (this.errors.brand_name) {
        this.$delete(this.errors, "brand_name");
      }
    },
    "form.sku_id"() {
      if (this.errors.sku_id) {
        this.$delete(this.errors, "sku_id");
      }
    },
    "form.description"() {
      if (this.errors.description) {
        this.$delete(this.errors, "description");
      }
    },
    "form.price"() {
      if (this.errors.price) {
        this.$delete(this.errors, "price");
      }
    },
    "form.stock"() {
      if (this.errors.stock) {
        this.$delete(this.errors, "stock");
      }
    },
    "form.category_id"() {
      if (this.errors.category_id) {
        this.$delete(this.errors, "category_id");
      }
    },
    "form.tag_ids"() {
      if (this.errors.tag_ids) {
        this.$delete(this.errors, "tag_ids");
      }
    },
    "form.unit_id"() {
      if (this.errors.unit_id) {
        this.$delete(this.errors, "unit_id");
      }
    },
    "form.packing_type_id"() {
      if (this.errors.packing_type_id) {
        this.$delete(this.errors, "packing_type_id");
      }
    },
    "form.commission"() {
      if (this.errors.commission) {
        this.$delete(this.errors, "commission");
      }
    },
    "form.length"() {
      if (this.errors.length) {
        this.$delete(this.errors, "length");
      }
    },
    "form.width"() {
      if (this.errors.width) {
        this.$delete(this.errors, "width");
      }
    },
    "form.height"() {
      if (this.errors.height) {
        this.$delete(this.errors, "height");
      }
    },
    "form.weight"() {
      if (this.errors.weight) {
        this.$delete(this.errors, "weight");
      }
    },
    "form.ingredients"() {
      if (this.errors.ingredients) {
        this.$delete(this.errors, "ingredients");
      }
    },
    "form.benefits"() {
      if (this.errors.benefits) {
        this.$delete(this.errors, "benefits");
      }
    },
    "form.about"() {
      if (this.errors.about) {
        this.$delete(this.errors, "about");
      }
    },
    "form.dosage"() {
      if (this.errors.dosage) {
        this.$delete(this.errors, "dosage");
      }
    },
    "form.precautions"() {
      if (this.errors.precautions) {
        this.$delete(this.errors, "precautions");
      }
    },
    "form.tagline"() {
      if (this.errors.tagline) {
        this.$delete(this.errors, "tagline");
      }
    },
    "form.note"() {
      if (this.errors.note) {
        this.$delete(this.errors, "note");
      }
    },
    "form.product_id"() {
      if (this.errors.product_id) {
        this.$delete(this.errors, "product_id");
      }
    },

    productItems(newVal) {
      console.log("productItems watcher triggered with:", newVal);
      if (newVal.length && this.$route.params.productId) {
        const decryptedId = Number(this.decryptIt(this.$route.params.productId));
        console.log("productItems watcher setting form.product_id to:", decryptedId);
        this.form.product_id = decryptedId;
      }
    },
  },
};
</script>

<style scoped>
/* Professional Form Card with Integrated Header */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

.professional-field >>> .v-textarea textarea {
  font-size: 0.75rem;
  line-height: 1.4;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }

  .form-title {
    font-size: 0.9rem;
  }

  .section-title {
    font-size: 0.85rem;
  }

  .form-content {
    padding: 0.75rem !important;
  }

  .back-button,
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .v-row > .v-col {
    padding: 0 4px;
  }

  .form-content {
    padding: 0.5rem !important;
  }

  .section-title {
    font-size: 0.8rem;
  }

  .professional-field >>> .v-input__control {
    min-height: 36px;
  }

  .professional-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .back-button,
  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Consistent Margins */
.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

/* Vue Editor Styling */
.editor-field-container {
  margin-bottom: 0.75rem;
}

.editor-label {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.professional-editor >>> .quill {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-family: inherit;
  font-size: 0.75rem;
}

.professional-editor >>> .ql-toolbar {
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
  border-radius: 4px 4px 0 0;
}

.professional-editor >>> .ql-container {
  border-radius: 0 0 4px 4px;
  font-size: 0.75rem;
  min-height: 120px;
}

.professional-editor >>> .ql-editor {
  min-height: 100px;
  padding: 12px;
  line-height: 1.4;
}

.professional-editor >>> .ql-editor.ql-blank::before {
  color: #9e9e9e;
  font-style: normal;
  font-size: 0.75rem;
}

.professional-editor.error--text >>> .quill {
  border-color: #f44336;
}

.professional-editor >>> .ql-toolbar .ql-formats {
  margin-right: 0.5rem;
}

.professional-editor >>> .ql-toolbar .ql-picker-label {
  font-size: 0.7rem;
}

.professional-editor >>> .ql-snow .ql-tooltip {
  z-index: 9999;
}

/* Error message styling to match Vuetify */
.v-messages {
  font-size: 0.7rem;
  min-height: 14px;
  padding-top: 4px;
}

.v-messages__wrapper {
  display: flex;
  flex-direction: column;
}

.v-messages__message {
  line-height: 1.2;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card,
.professional-editor {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Success Dialog Styling */
.success-header {
  background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
  border-bottom: 1px solid #4caf50;
}

.success-header .text-h6 {
  font-weight: 600;
  color: #2e7d32;
}

/* Dialog image upload section */
.v-dialog .form-section {
  background: transparent;
  border-radius: 0;
  padding: 0;
}

.v-dialog .upload-card {
  border-color: #e0e0e0;
}

.v-dialog .upload-card:hover {
  border-color: var(--v-primary-base);
}
</style>